import {
  IDefaultContabilCnpjResponse,
  RegimeTributarioEnum
} from "@/features/contabil/@types/types";
import { IDateRangeParams } from "../../types";
import { IEmpresaResponse } from "../../shared/requests/types";

export interface IGetDashboardResponse {
  certidoes: {
    pendentes: number | null;
  };
}

export interface IGetAutidoriaWarningResponse {
  titulo: string | null;
  mensagem: string | null;
  ano: string | null;
  tags: string[];
  anosVigentes: string[];
  cor: string | null;
}

export interface IGetAuditoriaResponse extends IEmpresaResponse {
  warnings: IGetAutidoriaWarningResponse[];
}

export interface IAuditoriaParams {
  empresasIds: string[];
}

export interface IFormasTributacaoParams {
  empresaId: string;
}

export interface IDetalhesFormasTributacaoParams {
  empresasId?: IFormasTributacaoParams[];
}

export interface IEnquadramentoSimples {
  situacao: string;
  enquadramentos: { dataInicial: string; dataFinal: string | null }[];
}

export interface IGetHistoricoRegimeTributarioResponse
  extends IEmpresaResponse {
  historicoRegimeTributario: Record<string, number> | null;
  enquadramentoSimples: IEnquadramentoSimples | null;
}

export interface IGetDetalhesFormaTributacaoResponse {
  empresas: IGetHistoricoRegimeTributarioResponse[];
}

export interface IQuadroSocietarioMatrizesDeclaradoResponse {
  cadastralSituation: string | null;
  percentual: number | null;
  country: string | null;
  id: string | null;
  name: string | null;
  personQualification: string | null;
  qualification: string | null;
  votingCapital: string | null;
  taxFreeIncome: number;
  taxableIncome: number;
  withholdingTax: number;
}

export interface IQuadroSocietarioMatrizesResponse {
  declarado: IQuadroSocietarioMatrizesDeclaradoResponse[] | null;
  receita: null;
  id: string;
  nomeFantasia: string | null;
  razaoSocial: string | null;
  regimeTributario: RegimeTributarioEnum;
  cnpj: IDefaultContabilCnpjResponse;
  updatedAt: string | null;
  quadroSocietarioNaoObrigatorio?: boolean;
}

export interface IQuadroSocietarioResponse {
  matrizes: IQuadroSocietarioMatrizesResponse[] | null;
  ano: string | null;
}

export interface IQuadroSocietarioParams {
  ano: string;
}

export interface IObrigacoesParams {
  empresas: string[];
}

export interface ITributosFaturamentoParams {
  ano?: string;
  mes?: string;
  empresas?: string[];
}

export interface ITributosAcumuladosParams {
  range: IDateRangeParams;
  empresas: string[];
}

export interface IFaturamentoParams {
  range: IDateRangeParams;
  empresas: string[];
}

type ILimiteFaturamentoSimples = IEmpresaResponse[] | null;
type ISublimiteFaturamentoSimples = IEmpresaResponse[] | null;

export interface IFaturamentoResponseInsights {
  percentageDeclaradoxCupom: number;
  somatoriaVendas: number;
  percentageDiferencaPeriodoAnterior: number;
  faturamentoMedio: number;
  evolucaoFaturamentoxTributo: number;
  percentageDocumentoFiscalxFinancas: number;
  limiteFaturamentoSimples: ILimiteFaturamentoSimples;
  sublimiteFaturamentoSimples: ISublimiteFaturamentoSimples;
}

export interface IDeclaradoPorEmpresa extends IEmpresaResponse {
  certificadoInvalido: boolean;
  empresaId: string;
  declarado: IFaturamentoResponseValue[] | null;
  previsoes: IFaturamentoPrevisoesResponse[];
}

export interface IFaturamentoResponseValue {
  data: string;
  valorDeclarado: number | null;
}

export interface IFaturamentoPrevisoesResponse {
  valorPrevisto: number;
  data: string;
}

export interface IFaturamentoResponse {
  documentoFiscal?: IFaturamentoResponseValue[];
  declarado?: IFaturamentoResponseValue[];
  financas?: IFaturamentoResponseValue[];
  insights?: IFaturamentoResponseInsights;
  declaradoPorEmpresa?: IDeclaradoPorEmpresa[];
}

export enum StatusSituacaoFiscalResponse {
  None,
  Pendencia,
  NenhumaPendencia,
  Aviso
}

export interface IReceitaFederalSituacaoFiscalDebitosResponse {
  descricaoReceita: string | null;
  valorDevido: number;
}

export interface IReceitaFederalSituacaoFiscalResponse {
  dataConsulta: string | null;
  debitosFiscais: IReceitaFederalSituacaoFiscalDebitosResponse[];
  debitosPrevidenciarios: IReceitaFederalSituacaoFiscalDebitosResponse[];
  status: StatusSituacaoFiscalResponse;
  exigibilidadeSuspensa: boolean;
  ausenciaDeDeclaracao: boolean;
  debitoTotalFiscal: number;
  debitoTotalPrevidenciario: number;
  alertaMensagemCaixaPostal: boolean;
}

export enum ReceitaFederalCadinTiposDebitosResponse {
  None,
  Possivel,
  ARegulariza,
  Motivador
}

export interface IReceitaFederalCadinDebitosResponse {
  cnpjDevedorOriginal: IDefaultContabilCnpjResponse;
  codigoReceita: string | null;
  competencia: string | null;
  vencimento: string | null;
  saldoDevedor: number;
  dataDeComunicacao: string | null;
  tipoDeDebito: ReceitaFederalCadinTiposDebitosResponse;
}

export enum ReceitaFederalStatusCadin {
  None,
  ComDebitos,
  IncluidoNoCadin,
  NaoIncluidoNoCadin,
  SuspensoPelaRFB,
  ExcluidoPelaRFB,
  Aviso
}

export enum ReceitaFederalCertificadoResponse {
  None,
  Valido,
  Expirado,
  Revogado,
  CpfDiferente,
  DownloadNaoRealizado,
  Inapto,
  Vencendo
}

export interface IReceitaFederalCadinResponse {
  status: ReceitaFederalStatusCadin;
  data: string;
  debitos: [];
  debitoTotal: number;
  alertaMotivara: boolean;
}

export interface ICertificadoEmpresaResponse {
  inicioValidade: string;
  terminoValidade: string;
  status: ReceitaFederalCertificadoResponse;
}

export interface IReceitaFederalEmpresaResponse extends IEmpresaResponse {
  situacaoFiscal: IReceitaFederalSituacaoFiscalResponse | null;
  cadin: IReceitaFederalCadinResponse | null;
  situacaoFiscalFile: string | null;
  cadinFile: string | null;
  certificadoDeEmpresa: ICertificadoEmpresaResponse | null;
}

export interface IReceitaFederalResponse {
  empresas: IReceitaFederalEmpresaResponse[];
}

export enum EObrigacoesFiscaisEmpresasObrigacoesStatus {
  None,
  Entregue,
  NaoEntregue,
  Desconhecido
}

export interface IObrigacoesFiscaisEmpresasObrigacoes {
  [periodo: string]: EObrigacoesFiscaisEmpresasObrigacoesStatus;
}

export type ObrigacoesPorMes = Record<
  string,
  IObrigacoesFiscaisEmpresasObrigacoes
>;

export type ObrigacoesData = IObrigacoesFiscaisEmpresasObrigacoes;

export interface IObrigacoesFiscaisEmpresas extends IEmpresaResponse {
  obrigacoes: Record<string, ObrigacoesPorMes | ObrigacoesData> | null;
}

export interface IObrigacoesFiscaisResponse {
  notificacoes: Record<number, number>;
  empresas: IObrigacoesFiscaisEmpresas[];
}

export interface ITributosFaturamentoEmpresasDctfResponse {
  declaradoPis: number | null;
  declaradoCofins: number | null;
  diferencaPis: number | null;
  diferencaCofins: number | null;
  declaradoPisDctf: number | null;
  declaradoCofinsDctf: number | null;
}

export interface ITributosFaturamentoEmpresasSimplesResponse {
  pago: number | null;
  devido: number | null;
  diferenca: number | null;
}

export interface ITributosFaturamentoEmpresasResponse extends IEmpresaResponse {
  dctf?: ITributosFaturamentoEmpresasDctfResponse;
  simples?: ITributosFaturamentoEmpresasSimplesResponse;
  certificadoInvalido: boolean;
}

export interface ITributosFaturamentoResponse {
  empresas: ITributosFaturamentoEmpresasResponse[];
}

export interface ITributosAcumuladosEmpresasResponse {
  id: string;
  cnpj: IDefaultContabilCnpjResponse;
  nomeFantasia: string;
  razaoSocial: string;
  dctf?: ITributosFaturamentoEmpresasDctfResponse;
  simples?: ITributosFaturamentoEmpresasSimplesResponse;
}

export interface ITributosAcumuladosEmpresaResponse extends IEmpresaResponse {
  totalCofins?: number;
  totalPis?: number;
  totalSimples?: number;
}

export interface IEmpresaMaisPagouResponse {
  totalPis: number;
  totalCofins: number;
  totalSimples: number;
  id: string;
  nomeFantasia: string | null;
  razaoSocial: string | null;
  regimeTributario: RegimeTributarioEnum;
  cnpj: IDefaultContabilCnpjResponse;
}

export interface ITributosAcumuladosPrevioesPorEmpresa
  extends IEmpresaResponse {
  totalPis: number;
  totalCofins: number;
  totalSimples: number | null;
  certificadoInvalido: boolean;
}

export interface ITributosAcumuladosResponse {
  mensagemComparativaAnoMes: boolean | null;
  tributoAcumuladoAno: number;
  variacaoPorcentagem: number;
  empresas: ITributosAcumuladosEmpresaResponse[];
  previsoesPorEmpresas: ITributosAcumuladosPrevioesPorEmpresa[];
  empresaQueMaisPagou: IEmpresaMaisPagouResponse;
}

export interface IRPJParams {
  year: string;
  empresas: string[];
}

export interface IEmpresaIRPJResponseModel {
  aPagarDctf: number | null;
  aPagarEcf: number | null;
  diferenca: number | null;
}

export interface IEmpresaCsllModel {
  aPagarDctf: number;
  aPagarEcf: number;
  diferenca: number;
}

export interface IEmpresaIRPJResponse extends IEmpresaResponse {
  irpj: IEmpresaIRPJResponseModel | null;
  csll: IEmpresaCsllModel | null;
}

export interface IRPJResponse {
  empresas: IEmpresaIRPJResponse[];
  diferencaPositiva: number;
  diferencaNegativa: number;
  contagemIrpj: number;
  contagemCsll: number;
}

export interface ITributosRetidosEmpresaDiferencas {
  dctf: number;
  fontesPagadoras: number;
  total: number | null;
}

export interface ITributosRetidosEmpresaResponse extends IEmpresaResponse {
  diferencas: Record<string, ITributosRetidosEmpresaDiferencas> | null;
}

export interface ITributosRetidosResponse {
  totalDiferencas: number;
  empresas: ITributosRetidosEmpresaResponse[];
  creditoDisponivel: number;
}

export interface IEvolucaoPatrimonioBalancoModelResponse {
  ativoTotal: number;
  passivoTotal: number;
  patrimonioLiquido: number;
  ativoCirculante: number;
  ativoNaoCirculante: number;
  passivoCirculante: number;
  passivoNaoCirculante: number;
  custoEDespesas: number;
  receita: number;
  lucroLiquidoExercicio: number;
}
export interface IEvolucaoPatrimonioBalanco {
  [year: string]: IEvolucaoPatrimonioBalancoModelResponse;
}

export interface IEvolucaoPatrimonioResponse {
  empresasComPatrimonioLiquidoNegativo: IEmpresaResponse[];
  empresasComPassivoEAtivoZero: number;
  dados: IEvolucaoPatrimonioBalanco;
  justificativaParimonial: IEmpresaResponse[] | null;
  justificativaResultado: IEmpresaResponse[] | null;
  semDemonstracao: string | null;
}

export interface IEvolucaoPatrimonioParams {
  empresas: string[];
}

export interface IIndicesEmpresasResponse extends IEmpresaResponse {
  indices: Record<string, IIndiceModelResponse>;
}

export interface IIndiceModelResponse {
  liquidezCorrente: number;
  liquidezSeca: number;
  liquidezImediata: number;
  liquidezGeral: number;
  rentabilidade: number;
  roe: number;
  ebtida: number;
}

export interface IIndicesResponse {
  empresas: IIndicesEmpresasResponse[];
}

export interface IIndicesParams {
  empresas: string[];
}

export interface ICabecalhoResponse {
  certificadosInvalidos: number;
  regimesCounter: Record<
    "LucroPresumido" | "LucroReal" | "None" | "Simples",
    number
  >;
}

export interface ICabecalhoParams {
  regimes: RegimeTributarioEnum[];
}

export interface ITributosSobreLucroEmpresas {
  aliquotaEfetiva: number;
  sumIrpj: number;
  sumCsll: number;
}

export interface ITributosSobreLucroResponse {
  empresas: ITributosSobreLucroEmpresas[];
  totalTributosDoGrupo: number;
  year: number;
}

export interface ITributosSobreLucroParams {
  empresas: string[];
  year: string;
}

export interface ITributosRetidosParams {
  empresas: string[];
}
